/**
 * SaaS sign-in screen (round 46; a Lit island since round 81). Deliberately NOT a router
 * page: navigateTo() pairs every page with a [data-page] nav link, and the signed-out state
 * has no nav at all - so this renders into the standalone #login-screen overlay, the .layout
 * shell stays hidden, and the SPA proper never boots until a session cookie exists. Success
 * paths end in a full document load at "/": the whole boot sequence (meta → chrome → navigation)
 * then runs exactly once, signed in, instead of being replayed piecemeal here.
 *
 * ⚠ Round 137 gave this screen an ADDRESS of its own - `/signin` - because the landing page took
 * `/` for visitors with no session. Nothing here routes; the SPA fallback serves the shell for any
 * non-API path and `showLoginScreen()` still runs off `/api/meta`. What changed is that the success
 * path must NAVIGATE to "/" rather than reload, or a fresh session would sit on `/signin` forever.
 *
 * The three lanes mirror /api/auth: password (sign in / create account), the emailed
 * sign-in link, and Google. Each lane that the server cannot honor is shown disabled
 * with the reason (the house rule) - Google without a configured client, and the email
 * lanes say exactly where the link went while delivery is console-only.
 *
 * Why an element the app CREATES rather than one that ships in index.html: selfhost never
 * signs in, so the gate must cost nothing there - `showLoginScreen()` is the only thing
 * that constructs `<gh-login-screen>`, and it runs only on a saas server with no session.
 * `busy` is a state flag, not `setBusy()` - an imperative textContent swap destroys Lit's
 * text markers (round 75).
 *
 * SHADOW DOM (round 90, B.3f page-island phase): the gate's own `.login-*` chrome moved out of
 * `public/style.css` into `static styles` below, and the generic chrome comes from the
 * `sharedStyles.ts` kit (card/form/control/button/spinner + chrome primitives - the same six the
 * onboarding page adopted in round 89). `.login-screen` STAYS global: it styles the light
 * `#login-screen` overlay this element renders INTO, which is never inside the shadow root. The
 * link color (a bare `<a>`) and the `<h1>` wordmark size are carried in too - element-selector
 * rules do not cross the boundary, though the theme TOKENS do, so every color is already right.
 * `this.querySelector` became `this.renderRoot.querySelector`: the form controls moved from the
 * element's light children into its shadow root.
 */
import { css, html, nothing, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { GhElement } from "../components/ghElement.ts";
import {
	buttonStyles,
	cardStyles,
	chromeStyles,
	controlStyles,
	formStyles,
	resetStyles,
	spinnerStyles,
	focusStyles
} from "../components/sharedStyles.ts";
import { api } from "../api.ts";
import { t } from "../i18n.ts";
import { icon } from "../components/ghIcon.ts";
import { BRAND_FIRST, BRAND_LAST } from "../../../shared/brand.ts";

export interface AuthMeta {
	enabled: boolean;
	/** `email` is null for a GUEST (§AM) - an account started with "Get started" and not claimed yet */
	user: { email: string | null; name: string | null } | null;
	/** This session's workspace has not been claimed yet */
	guest?: boolean;
	/** Whether "Get started" without a signup is switched on (`GUEST_ACCOUNTS`) */
	guestStart?: boolean;
	/**
	 * Set while this browser is looking at the EXAMPLE workspace (`/example`): read-only, made up, and
	 * `signedIn` says whether the visitor has an account of their own to go back to.
	 */
	example?: { signedIn: boolean };
	/** Whether this session is the installation operator (workspace 1); selfhost: true */
	operator: boolean;
	google: boolean;
	emailDelivery: string;
}

@customElement("gh-login-screen")
export class LoginScreen extends GhElement {
	/** Set imperatively by showLoginScreen(); property-only (no string attribute caller) */
	@property({ attribute: false }) accessor auth: AuthMeta | undefined;
	/**
	 * ⚠ **Seeded from the PATH, so `/signup` opens on the create-account form** (2026-08-09, owner: the
	 * landing page's "Start free" *"shouldn't take the user to the log in window, but immediately get
	 * them into setting up an account. It's all about reducing clicks"*). Every landing CTA pointed at
	 * `/signin`, which defaults here to the sign-in form - so somebody who pressed a button promising a
	 * free account met a password prompt for an account they do not have, and had to find the toggle.
	 * **That is a wasted click AND the wrong mental model**, which is the worse half: the screen answered
	 * a different question than the button asked.
	 *
	 * `/signup` needs no server route for the same reason `/signin` does not - the SPA fallback serves
	 * the shell for any non-API path and the shell mounts this island when `/api/meta` reports no user.
	 * ⚠ The 401 reload in `api()` keeps pointing at `/signin`, deliberately: a session expiring mid-use
	 * is a person who HAS an account, and sending them to a signup form would be this bug inverted.
	 */
	@state() private accessor mode: "signin" | "signup" = location.pathname === "/signup" ? "signup" : "signin";
	/** One message slot under the form - { kind, text } so info (link sent) and errors share it */
	@state() private accessor message: { kind: "info" | "error"; text: string } | undefined;
	@state() private accessor busy = false;

	/**
	 * The wordmark is SPLIT here, like the sidebar's and the landing page's - see `BRAND_FIRST`'s doc in
	 * `shared/brand.ts` for why that derivation lives in one place. This screen rendered `BRAND_NAME` as
	 * one flat string until 2026-08-09 (owner report), so it had no `<strong>` for the weight contrast
	 * and nothing for the gradient's accent tail to land on: the product's name read as body text on the
	 * one screen that is nothing BUT the product's name. The rendered text still equals `BRAND_NAME` -
	 * `tests/brand.test.ts` strips the tags and checks.
	 *
	 * ⚠ **This reasoning is here rather than in an HTML comment inside the template, and that is a rule
	 * rather than a preference.** A comment inside an `html` template ships as a real DOM comment, so
	 * `tests/icons.test.ts` sees the warning glyph in it and fails - which is exactly what the first
	 * draft of this change did. CLAUDE.md carries the note; a backtick in there would terminate the
	 * template outright, the same hazard one step worse.
	 */
	static styles = [
		focusStyles,
		resetStyles,
		chromeStyles,
		buttonStyles,
		spinnerStyles,
		cardStyles,
		formStyles,
		controlStyles,
		css`
			:host {
				display: block;
			}
			/* Element-selector rules don't cross the shadow boundary - carry the two the gate draws:
			   the app link color (else a bare <a>, like the sign-in/create-account toggle, falls back
			   to the UA's default blue, which is close to unreadable on the dark surfaces) and the
			   brand wordmark size. .login-google is a .btn, so buttonStyles' higher-specificity color
			   wins over this on the Google link. */
			a {
				color: var(--accent-text);
				text-decoration: none;
			}
			a:hover {
				text-decoration: underline;
			}
			/* ── the gate's own chrome (login-only - MOVED out of public/style.css). .login-screen is
			   NOT here: it styles the light #login-screen overlay this element renders into, so it
			   stays in the global sheet. ── */
			.login-card {
				width: min(400px, 92vw);
				margin-bottom: 0;
				padding: 28px;
				/* The other half of the owner's 2026-08-09 report - see the glow token in
				   public/style.css. A flat panel on a lit field still reads as a rectangle drawn ON the
				   page; these two make it sit ABOVE the page, which is what "cooler" was about. Both are
				   Crew's sign-in card verbatim in this app's tokens, so the two doors match.
				   The fill runs from the raised surface toward the page background, so the card is
				   brightest where the wordmark is - and it is tokens rather than a tint, which is what
				   makes it flip with the theme instead of being a dark-mode gradient that turns a white
				   card gray. It overrides the flat fill composed in from the card fragment. */
				background: linear-gradient(180deg, var(--bg2) 0%, var(--bg) 190%);
				box-shadow: var(--shadow-modal);
			}
			.login-brand {
				display: flex;
				align-items: center;
				gap: 10px;
				margin-bottom: 6px;
			}
			.login-brand h1 {
				font-size: 1.25rem;
			}
			/* The product's promise, directly under the wordmark. It is the first line a stranger reads
			   on the signup screen, so it says what the app is FOR; the muted subtitle below it says
			   what the form does. Deliberately the stronger of the two, or the pair reads as one gray
			   mumble. */
			.login-tagline {
				color: var(--text);
				font-size: 0.9375rem;
				margin-bottom: 14px;
			}
			.login-logo {
				display: block;
				flex: none;
				width: 32px;
				height: 32px;
				background-color: var(--accent);
				-webkit-mask: url("/app_icon.svg") center / contain no-repeat;
				mask: url("/app_icon.svg") center / contain no-repeat;
			}
			/* ⚠ A SHADOW TWIN of public/style.css's .sidebar-header h1, copied rather than shared because
			   selector CSS stops at a shadow boundary and this island has its own root. Kept verbatim so
			   the two cannot drift - the gradient STARTS in the text color and only reaches the accent at
			   the tail, which is what makes the name read as the page's own text with the accent as a
			   highlight rather than as a tinted logo (owner's 2026-08-04 call, recorded there).
			   ⚠ The keyframes are DUPLICATED into this root too, not just referenced: an @keyframes is
			   tree-scoped, so a rule that animates inside a shadow root needs the animation defined in
			   that same root - spinnerStyles carries the identical note for .btn-busy. */
			@keyframes wordmark-sheen {
				from {
					background-position: 0% 50%;
				}
				to {
					background-position: 100% 50%;
				}
			}
			.login-card h1 {
				color: var(--text);
				background-image: linear-gradient(100deg, var(--text) 0%, var(--text) 46%, var(--accent-text) 88%, var(--accent-2) 100%);
				background-size: 220% 100%;
				-webkit-background-clip: text;
				background-clip: text;
				-webkit-text-fill-color: transparent;
				animation: wordmark-sheen var(--motion-sheen-loop) var(--motion-ease) var(--motion-decor-iterations) alternate;
				/* 500 so the <strong> beside it can supply the contrast - at 700 the emphasis vanishes. */
				font-weight: 500;
			}
			.login-card h1 strong {
				font-weight: 800;
			}
			.login-card .subtitle {
				margin-bottom: 18px;
			}
			/* ⚠ FLEX now, where this was a block with centered text (2026-08-09). That centers a lone text
			   node fine and cannot center an icon-plus-label PAIR - the mark and the words would sit as two
			   inline items with a text space between them and no shared baseline control. The gap replaces
			   that space, so the pair reads as one thing at any text scale.
			   ⚠ No backticks in this comment, and that is not fussiness: inside a css tagged template a
			   backtick ENDS the template. The first draft of this block quoted two property names that way
			   and broke the typecheck - CLAUDE.md's own gotcha, met a fourth time today. Name properties in
			   words in here. */
			.login-google {
				display: flex;
				align-items: center;
				justify-content: center;
				gap: 8px;
				width: 100%;
				margin-bottom: 4px;
			}
			.login-divider {
				display: flex;
				align-items: center;
				gap: 10px;
				color: var(--text2);
				font-size: 0.75rem;
				margin: 14px 0;
			}
			.login-divider::before,
			.login-divider::after {
				content: "";
				flex: 1;
				border-top: 1px solid var(--border);
			}
			.login-submit {
				width: 100%;
				margin-top: 4px;
			}
			.login-magic {
				width: 100%;
				margin-top: 10px;
			}
			.login-delivery-note {
				margin-top: 10px;
			}
			.login-msg {
				margin-top: 12px;
				font-size: 0.8125rem;
				padding: 8px 10px;
				border-radius: var(--radius);
				border: 1px solid var(--border);
			}
			.login-msg.error {
				color: var(--danger);
				border-color: var(--danger);
			}
			.login-msg.info {
				color: var(--success);
				border-color: var(--success);
			}
			.login-toggle {
				margin-top: 14px;
				text-align: center;
			}
			.login-guest {
				display: inline;
			}
		`
	];

	#input(id: string): string {
		// renderRoot is the shadow root now (round 90) - this.querySelector would find nothing,
		// the form controls moved out of the element's light children into shadow.
		return this.renderRoot.querySelector<HTMLInputElement>(`#${id}`)?.value.trim() ?? "";
	}

	#say(kind: "info" | "error", text: string): void {
		this.message = { kind, text };
	}

	async #submitPassword(): Promise<void> {
		if (this.auth == null) return;
		const email = this.#input("login-email");
		const password = this.renderRoot.querySelector<HTMLInputElement>("#login-password")?.value ?? "";
		const name = this.#input("login-name");
		this.busy = true;
		try {
			if (this.mode === "signup") {
				await api("/api/auth/signup", { method: "POST", body: { email, password, ...(name.length > 0 ? { name } : {}) } });
				this.#say("info", this.auth.emailDelivery === "console" ? t("login.msgAccountCreatedConsole") : t("login.msgAccountCreatedEmail"));
			} else {
				await api("/api/auth/login", { method: "POST", body: { email, password } });
				// To "/" rather than a reload: this screen now has its own address (/signin, round
				// 137's landing page took "/"), and a session that was just created belongs at the
				// app's root, not on the URL that asked for it. `replace` keeps the sign-in form out
				// of the Back history, where pressing Back would show a form that is already answered.
				location.replace("/");
			}
		} catch (e) {
			this.#say("error", e instanceof Error ? e.message : String(e));
		} finally {
			this.busy = false;
		}
	}

	/**
	 * ⚠ **`forgot` CHANGES THE SENTENCE AND NOTHING ELSE, and that is the whole design** (▶ WORK
	 * ORDER). There is no password-reset token in this app and there does not need to be: a magic
	 * link already proves control of the address, and `POST /api/auth/set-password` lets a signed-in
	 * user set a new one. **The recovery loop was complete and unfindable** - the only door to it was
	 * a button reading *Email me a sign-in link instead*, which never says the word "password", and
	 * somebody who has forgotten theirs is scanning for exactly that word.
	 *
	 * So this adds an AFFORDANCE rather than a mechanism - §AE round 5's rule, that a feature which
	 * only exists once you have guessed the right move is not one - and pointedly not a second lane
	 * beside a working one. What the forgot path owes the reader that the plain one does not is the
	 * NEXT step: a sign-in link is not a new password, and a message that stops at "check your email"
	 * leaves them expecting one.
	 */
	async #requestMagicLink(forgot = false): Promise<void> {
		if (this.auth == null) return;
		const email = this.#input("login-email");
		if (email.length === 0) {
			this.#say("error", t("login.errNeedEmail"));
			return;
		}
		try {
			await api("/api/auth/magic", { method: "POST", body: { email } });
			const console_ = this.auth.emailDelivery === "console";
			this.#say(
				"info",
				forgot
					? console_
						? t("login.msgForgotConsole", { email })
						: t("login.msgForgotEmail", { email })
					: console_
						? t("login.msgMagicConsole", { email })
						: t("login.msgMagicEmail", { email })
			);
		} catch (e) {
			this.#say("error", e instanceof Error ? e.message : String(e));
		}
	}

	protected render() {
		if (this.auth == null) return nothing;
		const signup = this.mode === "signup";

		// The mark rides on both variants, decorative in both: the label beside it already says Google,
		// so a `label` would make a screen reader announce the word twice (ghIcon's rule - label only
		// when the icon IS the only text).
		const googleLane = this.auth.google
			? html`<a class="btn login-google" href="/api/auth/google">${icon("brand-google")}<span>${t("login.google")}</span></a>`
			: html`<button class="btn login-google" disabled title=${t("login.googleTitle")}>
					${icon("brand-google")}<span>${t("login.googleUnconfigured")}</span>
				</button>`;

		return html`<div class="login-card card">
			<div class="login-brand">
				<span class="login-logo" aria-hidden="true"></span>
				<h1>${BRAND_FIRST} <strong>${BRAND_LAST}</strong></h1>
			</div>
			<p class="login-tagline">${t("brand.tagline")}</p>
			<p class="subtitle">${signup ? t("login.subtitleSignup") : t("login.subtitleSignin")}</p>
			${googleLane}
			<div class="login-divider"><span>${t("login.or")}</span></div>
			<form
				id="login-form"
				@submit=${(e: SubmitEvent) => {
					e.preventDefault();
					void this.#submitPassword();
				}}
			>
				${signup
					? html`<div class="form-group">
							<label>${t("login.nameLabel")} <span class="hint">${t("login.optional")}</span></label>
							<input type="text" id="login-name" autocomplete="name" placeholder=${t("login.namePlaceholder")} />
						</div>`
					: nothing}
				<div class="form-group">
					<label>${t("login.emailLabel")}</label>
					<input type="email" id="login-email" autocomplete="email" required placeholder=${t("login.emailPlaceholder")} />
				</div>
				<div class="form-group">
					<label>${t("login.passwordLabel")}${signup ? html` <span class="hint">${t("login.passwordHint")}</span>` : nothing}</label>
					<input
						type="password"
						id="login-password"
						autocomplete=${signup ? "new-password" : "current-password"}
						required
						minlength=${signup ? 8 : 1}
					/>
				</div>
				${
					/* ⚠ Sign-in only. On the SIGNUP form there is no password to have forgotten, and an
						   offer to recover an account somebody is in the middle of creating reads as the app
						   thinking they already have one. ⚠ `type="button"` is load-bearing inside a form -
						   the default is `submit`, so without it this posts the half-filled sign-in. */ ""
				}
				${signup
					? nothing
					: html`<button type="button" class="btn-link login-forgot" @click=${() => void this.#requestMagicLink(true)}>
							${t("login.forgotPassword")}
						</button>`}
				<button type="submit" class="btn btn-primary login-submit ${this.busy ? "btn-busy" : ""}" ?disabled=${this.busy}>
					${signup ? t("login.createAccount") : t("login.signin")}
				</button>
			</form>
			<button class="btn btn-small login-magic" @click=${() => void this.#requestMagicLink()}>${icon("mail")} ${t("login.magicLink")}</button>
			${this.auth.emailDelivery === "console" ? html`<p class="hint login-delivery-note">${t("login.deliveryNote")}</p>` : nothing}
			${this.message != null ? html`<p class="login-msg ${this.message.kind}">${this.message.text}</p>` : nothing}
			${signup || this.auth.guestStart !== true
				? html`<p class="hint login-toggle">
						${signup
							? html`${t("login.haveAccount")} <a href="#" @click=${this.#toggle}>${t("login.signin")}</a>`
							: html`${t("login.newHere")} <a href="#" @click=${this.#toggle}>${t("login.createOne")}</a>`}
					</p>`
				: this.#renderGuestStart()}
		</div>`;
	}

	/**
	 * §AM: "New here?" answers with the button that makes a workspace, the landing page's own, rather
	 * than a form asking for an address first (the owner: *"instead the buttons are 'Get Started'"*). A
	 * real form POST, because the server answers with a redirect into the app and a fetch would only
	 * read it. An address is what the claim asks for later, so /signup is no longer offered here.
	 */
	#renderGuestStart(): TemplateResult {
		return html`<div class="hint login-toggle">
			${t("login.newHere")}
			<form class="login-guest" method="post" action="/api/auth/guest">
				<button type="submit" class="btn-link" data-action="guest-start">${t("login.getStarted")}</button>
			</form>
		</div>`;
	}

	/** Arrow field so `this` binds when used directly as a `@click` handler */
	#toggle = (e: Event): void => {
		e.preventDefault();
		this.mode = this.mode === "signup" ? "signin" : "signup";
		this.message = undefined;
	};
}

/**
 * Reveal the sign-in gate and hide the app shell. Creates the element on first call (and
 * only ever on a saas server with no session - see the header), then hands it the auth
 * meta; a second call just refreshes the meta on the element already there.
 */
export function showLoginScreen(auth: AuthMeta): void {
	document.querySelector(".layout")?.classList.add("hidden");
	const root = document.getElementById("login-screen");
	if (root == null) return;
	root.classList.remove("hidden");
	let el = root.querySelector<LoginScreen>("gh-login-screen");
	if (el == null) {
		el = document.createElement("gh-login-screen") as LoginScreen;
		root.appendChild(el);
	}
	el.auth = auth;
}
